import React, { useRef, useState } from 'react';
import pluralize from 'pluralize';
import { FieldArray, Formik } from 'formik';
import { Form } from 'formik-antd';
import * as Yup from 'yup';
import { isNil } from 'lodash';

import BaseTableHeader from 'components/wrappers/BaseTableHeader';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import SubmitButton from 'components/formik/SubmitButton';
import {
  getListOfCustomersWithoutAgreement,
  isFileUploaderRequired,
} from 'utils/clientUtils';
import {
  CreateCustomerRequest,
  ServiceAgreementTypeEnum,
} from 'api/backofficeApiClient';
import { useAppSelector } from 'hooks/useAppSelector';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import ClientModal, {
  ClientInitialValues,
} from 'components/modals/ClientModal';
import { ClientCreationEnum } from 'components/modals/ClientModal/enum';
import ClientBulkImportTableForm from './ClientBulkImportTableForm';
import { mapCustomerDetailsToInitialValues } from '../../ClientDetailsPage/EditClient/utils';
import { BaseFileYupObject } from 'components/formik/FormikFileUpload';
import { CustomerBulkImportDetailsModel } from './interface';
import { mapCustomerBulkImportToRequest } from 'utils/bulkImportUtils';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { getCustomerGroups } from 'redux/reducers/clientSlice';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';

type Props = {
  onSubmit: (data: CreateCustomerRequest[]) => Promise<void>;
  prefilledCustomers: CustomerBulkImportDetailsModel[];
};

const agreementValidationSchema = Yup.object({
  agreementDetailsInfo: Yup.object({
    agreementAttachments: Yup.array().when('agreementDetailsType', {
      is: (agreementDetailsType: ServiceAgreementTypeEnum) =>
        isFileUploaderRequired(agreementDetailsType),
      then: Yup.array().of(BaseFileYupObject).min(1, 'Field is required'),
    }),
  }),
});

const validationSchema = Yup.object({
  customers: Yup.array().of(agreementValidationSchema),
});

export interface ClientBulkImportForm {
  customers: CustomerBulkImportDetailsModel[];
}

interface ClientModel {
  data: ClientInitialValues;
  clientId: string;
}

export default function ClientBulkImportTableSection(props: Props) {
  const { prefilledCustomers, onSubmit } = props;
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(isAdminSelector);
  const [loadingEditClientId, setLoadingEditClientId] = useState('');
  const [clientDetails, setClientDetails] = useState<ClientModel>();
  const initialValues: ClientBulkImportForm = {
    customers: prefilledCustomers,
  };
  const formRef: any = useRef();

  const handleEditClientModalOpen = async (
    clientDetails: CustomerBulkImportDetailsModel,
  ) => {
    const { id: clientId, ...clientDetailsWithoutId } = clientDetails;

    if (isAdmin) {
      setLoadingEditClientId(clientId);

      const result = await dispatch(getCustomerGroups());

      if (getCustomerGroups.fulfilled.match(result)) {
        const mappedClientDetailsToInitialValues =
          mapCustomerDetailsToInitialValues(
            clientDetailsWithoutId,
            ClientCreationEnum.EditImport,
          );

        setClientDetails({
          data: mappedClientDetailsToInitialValues,
          clientId,
        });
      } else {
        BaseAlert.error(result.error.message || ApiErrorMessage);
      }

      setLoadingEditClientId('');
    } else {
      const mappedClientDetailsToInitialValues =
        mapCustomerDetailsToInitialValues(
          clientDetailsWithoutId,
          ClientCreationEnum.EditImport,
        );

      setClientDetails({
        data: mappedClientDetailsToInitialValues,
        clientId,
      });
    }
  };

  const handleEditClientModalClose = () => {
    setClientDetails(undefined);
  };

  const customersMessage = pluralize(
    'Customer',
    prefilledCustomers?.length || 0,
    false,
  );

  const getAllCustomersCountMessage = () => {
    const allCustomerLength = prefilledCustomers?.length || 0;

    const msg = pluralize('Record', allCustomerLength, true);
    return msg + ' to be added';
  };

  const getCustomersCountMessage = () => {
    const customersLength =
      getListOfCustomersWithoutAgreement(initialValues.customers).length || 0;
    const msg = pluralize('Agreements', customersLength, true);

    return msg + ' to be uploaded';
  };

  const renderSubTitle = (
    <>
      <div>{getAllCustomersCountMessage()}</div>
      <div>{getCustomersCountMessage()}</div>
    </>
  );

  const handleSubmit = async (values: ClientBulkImportForm) => {
    const data = mapCustomerBulkImportToRequest(values);
    await onSubmit(data);
  };

  const onSetClientValues = (
    updatedCustomerValues: CreateCustomerRequest,
    customerId: string,
  ) => {
    const formProperties = formRef.current;
    const formValues: CustomerBulkImportDetailsModel[] =
      formProperties.values.customers || [];

    const updatedClientsDetails = formValues.map((customer) =>
      customer.id === customerId
        ? {
            id: customer.id,
            ...updatedCustomerValues,
            agreementDetailsInfo: {
              ...updatedCustomerValues.agreementDetailsInfo,
              agreementAttachments:
                updatedCustomerValues.agreementDetailsInfo.agreementAttachments?.map(
                  (file) => ({
                    fileName: file.fileName || '',
                    s3Key: file.fileKey || '',
                  }),
                ),
            },
          }
        : customer,
    );

    formProperties.setFieldValue('customers', updatedClientsDetails, false);
  };

  return (
    <>
      {!isNil(clientDetails) && (
        <ClientModal
          onSetClientValues={onSetClientValues}
          onClose={handleEditClientModalClose}
          creationType={ClientCreationEnum.EditImport}
          initialValues={clientDetails.data}
          customerId={clientDetails.clientId}
          isDraftAvailable={false}
        />
      )}

      <BasePageLabel label="Upload agreements" />

      <div className="page-box page-inner">
        <div className="page-inner-container">
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            innerRef={formRef}
          >
            <Form>
              <BaseTableHeader
                title={customersMessage}
                enableSubTitle={true}
                subTitle={renderSubTitle}
              >
                <div>
                  <SubmitButton>Import</SubmitButton>
                </div>
              </BaseTableHeader>
              <FieldArray
                name="customers"
                render={(props) => (
                  <ClientBulkImportTableForm
                    {...props}
                    isAdmin={isAdmin as boolean}
                    loadingEditClientId={loadingEditClientId}
                    handleEditClientModalOpen={handleEditClientModalOpen}
                  />
                )}
              />
            </Form>
          </Formik>
        </div>
      </div>
    </>
  );
}
